// SuspenseLoader.tsx
import { useEffect, useState, useMemo } from "react";

type LoaderType = "property" | "page" | "data" | "search";

interface LoaderConfig {
  icon: string;
  accent: string;
  secondary: string;
  label: string;
  duration: number;
  pattern: LoaderType;
  messages: string[];
}

interface GenericLoaderProps {
  message?: string;
  type?: LoaderType;
  className?: string;
  appendMessages?: string[];
  messagesArray?: string[];
  customLabel?:string
}

interface Coordinates {
  x: number;
  y: number;
}

export default function GenericLoader({
  message = "Loading",
  type = "page",
  className = "",
  appendMessages = [],
  messagesArray = [],
  customLabel
}: GenericLoaderProps) {
  const [phase, setPhase] = useState<number>(0);
  const [coordinates, setCoordinates] = useState<Coordinates>({ x: 50, y: 50 });

  const config: LoaderConfig = useMemo(() => {
    const types: Record<LoaderType, LoaderConfig> = {
      property: {
        icon: "⌂",
        accent: "var(--color-primary)",
        secondary: "var(--color-secondary)",
        label: "PROPERTY",
        duration: 4000,
        pattern: "property",
        messages: messagesArray.length
          ? [...messagesArray, ...appendMessages]
          : [
              "Scanning location",
              "Analyzing structure",
              "Valuing asset",
              ...appendMessages,
            ],
      },
      page: {
        icon: "▤",
        accent: "var(--color-text-main)",
        secondary: "var(--color-text-muted)",
        label: "PAGE",
        duration: 3000,
        pattern: "page",
        messages: messagesArray.length
          ? [...messagesArray, ...appendMessages]
          : [
              "Composing layout",
              "Arranging elements",
              "Finalizing view",
              ...appendMessages,
            ],
      },
      data: {
        icon: "⇅",
        accent: "var(--color-status-error)",
        secondary: "var(--color-primary)",
        label: "DATA",
        duration: 2500,
        pattern: "data",
        messages: messagesArray.length
          ? [...messagesArray, ...appendMessages]
          : [
              "Fetching records",
              "Processing information",
              "Syncing updates",
              ...appendMessages,
            ],
      },
      search: {
        icon: "◎",
        accent: "var(--color-ring-main)",
        secondary: "var(--color-text-muted)",
        label: "SEARCH",
        duration: 3500,
        pattern: "search",
        messages: messagesArray.length
          ? [...messagesArray, ...appendMessages]
          : [
              "Exploring radius",
              "Matching criteria",
              "Filtering results",
              ...appendMessages,
            ],
      },
    };
    return types[type];
  }, [appendMessages, messagesArray, type]);

  useEffect(() => {
    const phaseInterval = setInterval(() => {
      setPhase((prev) => (prev + 1) % 100);
    }, config.duration / 100);

    return () => clearInterval(phaseInterval);
  }, [config.duration]);

  useEffect(() => {
    if (config.pattern !== "search") return;

    const coordInterval = setInterval(() => {
      setCoordinates({
        x: 20 + Math.random() * 60,
        y: 20 + Math.random() * 60,
      });
    }, config.duration / 4);

    return () => clearInterval(coordInterval);
  }, [config.pattern, config.duration]);

  const messageIndex = Math.floor(phase / 33.4);
  const currentMessage =
    config.messages[Math.min(messageIndex, config.messages.length - 1)];

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${className}`}
    >
      {/* Atmospheric backdrop */}
      <div className="bg-bg/10 absolute inset-0 backdrop-blur-[2px]" />

      {/* Main stage */}
      <div className="relative flex flex-col items-center">
        {/* Dynamic canvas area */}
        <div className="relative h-40 w-40">
          {/* Property type: Architectural cross-section */}
          {config.pattern === "property" && (
            <>
              {/* Foundation lines */}
              <div className="bg-secondary/30 absolute bottom-5 left-1/2 h-px w-3/4 -translate-x-1/2" />
              <div className="bg-secondary/20 absolute bottom-7 left-1/2 h-px w-1/2 -translate-x-1/2" />

              {/* Building outline forming */}
              <div
                className="absolute bottom-5 left-1/2 -translate-x-1/2 transition-all duration-1000"
                style={{
                  width: `${Math.min(phase * 2, 60)}%`,
                  height: `${Math.min(phase * 1.5, 45)}%`,
                  border: `1px solid ${config.accent}`,
                  borderBottom: "none",
                  opacity: phase > 10 ? 1 : 0.2,
                }}
              />

              {/* Windows lighting up */}
              {[0, 1, 2, 3].map((windowIndex) => (
                <div
                  key={windowIndex}
                  className="absolute transition-all duration-500"
                  style={{
                    bottom: `${30 + windowIndex * 8}%`,
                    left: `${25 + (windowIndex % 2) * 25}%`,
                    width: "15%",
                    height: "6%",
                    background:
                      phase > 20 + windowIndex * 10
                        ? config.accent
                        : "transparent",
                    opacity: phase > 20 + windowIndex * 10 ? 0.8 : 0,
                  }}
                />
              ))}
            </>
          )}

          {/* Page type: Layered sheets assembling */}
          {config.pattern === "page" && (
            <>
              {[0, 1, 2].map((layer) => (
                <div
                  key={layer}
                  className="absolute transition-all duration-700"
                  style={{
                    top: `${20 + layer * 15}%`,
                    left: `${25 + layer * 5}%`,
                    width: `${50 - layer * 10}%`,
                    height: `${40 - layer * 5}%`,
                    border: `1px solid ${layer === 0 ? config.accent : config.secondary}`,
                    transform: `rotate(${layer * 2}deg) translateY(${
                      phase > 30 + layer * 20 ? 0 : 10
                    }px)`,
                    opacity: phase > 20 + layer * 15 ? 1 : 0.3,
                  }}
                />
              ))}

              {/* Folding line animation */}
              <div
                className="absolute top-1/4 left-1/2 h-1/2 w-px transition-all duration-1000"
                style={{
                  background: `linear-gradient(to bottom, transparent, ${config.accent}, transparent)`,
                  transform: `scaleY(${phase % 50 > 25 ? 1 : 0.3})`,
                  opacity: 0.6,
                }}
              />
            </>
          )}

          {/* Data type: Binary streams */}
          {config.pattern === "data" && (
            <>
              {/* Data columns */}
              {[0, 1, 2].map((col) => (
                <div
                  key={col}
                  className="absolute top-0 flex flex-col gap-1"
                  style={{ left: `${25 + col * 25}%` }}
                >
                  {[0, 1, 2, 3, 4].map((bit) => (
                    <div
                      key={bit}
                      className="h-4 w-8 rounded-sm transition-all duration-300"
                      style={{
                        background:
                          (phase + col * 10 + bit * 5) % 50 > 25
                            ? config.accent
                            : config.secondary,
                        opacity:
                          (phase + col * 10 + bit * 5) % 50 > 25 ? 0.8 : 0.2,
                        transform: `translateY(${Math.sin((phase + bit * 20) * 0.1) * 3}px)`,
                      }}
                    />
                  ))}
                </div>
              ))}

              {/* Connection lines */}
              <svg className="absolute inset-0 h-full w-full">
                <line
                  x1="10%"
                  y1="50%"
                  x2="90%"
                  y2="50%"
                  stroke={config.secondary}
                  strokeWidth="0.5"
                  strokeDasharray="2,2"
                  opacity="0.3"
                />
              </svg>
            </>
          )}

          {/* Search type: Radar sweep with target lock */}
          {config.pattern === "search" && (
            <>
              {/* Radar circles */}
              <div className="border-secondary/20 absolute inset-[10%] rounded-full border" />
              <div className="border-secondary/30 absolute inset-[25%] rounded-full border" />
              <div className="border-secondary/40 absolute inset-[40%] rounded-full border" />

              {/* Sweep line */}
              <div
                className="absolute inset-0 animate-[spin_2s_linear_infinite]"
                style={{
                  background: `conic-gradient(from 0deg, transparent 0deg, ${config.accent} 30deg, transparent 60deg)`,
                  borderRadius: "50%",
                  opacity: 0.3,
                }}
              />

              {/* Target acquisition */}
              <div
                className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 transition-all duration-500"
                style={{
                  left: `${coordinates.x}%`,
                  top: `${coordinates.y}%`,
                }}
              >
                <div className="bg-status-error/50 absolute inset-0 animate-ping rounded-full" />
                <div className="bg-status-error relative h-full w-full rounded-full" />
              </div>

              {/* Crosshair */}
              <div
                className="absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 transition-all duration-300"
                style={{
                  left: `${coordinates.x}%`,
                  top: `${coordinates.y}%`,
                  border: `1px solid ${config.accent}`,
                  borderRadius: "50%",
                }}
              />
            </>
          )}

          {/* Floating icon */}
          <div
            className="absolute -top-4 -right-4 font-serif text-2xl transition-all duration-1000"
            style={{
              color: config.accent,
              transform: `translateY(${Math.sin(phase * 0.2) * 5}px) rotate(${Math.sin(phase * 0.3) * 10}deg)`,
              opacity: 0.7,
            }}
          >
            {config.icon}
          </div>
        </div>

        {/* Bottom information */}
        <div className="mt-6 flex flex-col items-center gap-3">
          {/* Type label */}
          <span
            className="rounded-full px-4 py-1 font-mono text-[10px] tracking-[0.3em] uppercase"
            style={{
              background: `${config.accent}10`,
              color: config.accent,
              border: `1px solid ${config.accent}30`,
            }}
          >
            {customLabel || config.label}
          </span>

          {/* Dynamic message */}
          <div className="relative h-6 overflow-hidden">
            <p
              className="text-text-muted font-sans text-sm transition-all duration-500"
              style={{
                transform: `translateY(-${messageIndex * 24}px)`,
              }}
            >
              {config.messages.map((msg, index) => (
                <span key={index} className="block h-6 text-center">
                  {msg}
                </span>
              ))}
            </p>
          </div>

          {/* Progress indicator */}
          <div className="bg-secondary/20 relative h-px w-32 overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full transition-all duration-100"
              style={{
                width: `${phase}%`,
                background: `linear-gradient(to right, transparent, ${config.accent})`,
              }}
            />
          </div>

          {/* Custom message override */}
          {message !== "Loading" && (
            <p className="text-text-muted/70 font-serif text-xs italic">
              {message || currentMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
